import { Fragment, useState } from 'react';
import { Link } from 'react-router';

import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { DownloadIcon } from '@/shared/ui/icons';
import { Select } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { Stat } from '@/shared/ui/Stat';
import { Table, TBody, Td, Th, THead, Tr } from '@/shared/ui/Table';
import { TechnicalDetails } from '@/shared/ui/TechnicalDetails';
import { formatDate } from '@/shared/utils/format';

import { errorsFileUrl } from './api';
import type { ErrorEvent, ErrorFilters, ErrorGroup } from './types';
import { useErrorOccurrences, useErrors } from './useAdmin';

const WINDOWS = [
  { days: 1, label: 'Último día' },
  { days: 7, label: 'Últimos 7 días' },
  { days: 30, label: 'Últimos 30 días' },
  { days: 90, label: 'Últimos 90 días' },
] as const;

const PROCESSES = [
  { value: 'worker', label: 'Worker (clasificar, leer, enviar)' },
  { value: 'connector', label: 'Conector (traer el correo)' },
  { value: 'api', label: 'API (la pantalla)' },
] as const;

const whenFormat = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' });

/** From a number of days back to the instant the API filters on. Computed when the
 * window changes, never on render: the instant is part of the query key. */
function sinceFor(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * Administración → Incidencias (contract v10.31, ADR 0028 of the back): what keeps
 * failing, to fix it.
 *
 * Every warning and error the three processes log is kept in `error_events`, without the
 * content of any email. This screen reads it grouped: one row per failure, the most
 * frequent first, with how many emails it touched, since when, in which process and
 * which deploy. A row opens its occurrences, each with its email. The download is every
 * row of the window, for whoever distills them. Superadministrators only: this is the
 * internal text the rest of the application no longer shows.
 */
export function ErrorsTab() {
  const [days, setDays] = useState<number>(7);
  const [filters, setFilters] = useState<ErrorFilters>(() => ({ since: sinceFor(7) }));
  const [open, setOpen] = useState<string | null>(null);
  const summary = useErrors(filters);

  function change(patch: Partial<ErrorFilters>) {
    setOpen(null);
    setFilters((current) => ({ ...current, ...patch }));
  }

  const data = summary.data;
  const codes = data?.by_code ?? [];
  return (
    <div className="flex flex-col gap-4">
      <p className="text-ink-2 text-[14px] leading-relaxed">
        Cada aviso o error que registra la aplicación, agrupado: lo que más falla primero, a cuántos
        correos afectó y desde cuándo. Lo que la gente ve en pantalla es el{' '}
        <strong className="text-ink">código</strong> (por ejemplo LEC-01); aquí está lo que hay
        detrás. Sin el contenido de ningún correo. Se guarda 90 días.
      </p>

      <form
        className="grid items-end gap-3 sm:grid-cols-3"
        aria-label="Filtros de las incidencias"
        onSubmit={(event) => event.preventDefault()}
      >
        <Select
          label="Periodo"
          value={String(days)}
          onChange={(event) => {
            const next = Number(event.target.value);
            setDays(next);
            change({ since: sinceFor(next) });
          }}
        >
          {WINDOWS.map((window) => (
            <option key={window.days} value={window.days}>
              {window.label}
            </option>
          ))}
        </Select>
        <Select
          label="Código"
          value={filters.code ?? ''}
          onChange={(event) => change({ code: event.target.value || undefined })}
        >
          <option value="">Todos</option>
          {codes
            .filter((entry) => entry.code)
            .map((entry) => (
              <option key={entry.code} value={entry.code ?? ''}>
                {entry.code} ({entry.count})
              </option>
            ))}
        </Select>
        <Select
          label="Proceso"
          value={filters.process ?? ''}
          onChange={(event) => change({ process: event.target.value || undefined })}
        >
          <option value="">Todos</option>
          {PROCESSES.map((process) => (
            <option key={process.value} value={process.value}>
              {process.label}
            </option>
          ))}
        </Select>
      </form>

      {summary.isPending ? (
        <Spinner label="Cargando incidencias…" />
      ) : summary.isError ? (
        <ErrorMessage error={summary.error}>No se pudieron cargar las incidencias</ErrorMessage>
      ) : data && data.groups.length === 0 ? (
        <EmptyState
          title="Sin incidencias en este periodo"
          hint="Nada ha registrado un aviso ni un error. Prueba con un periodo más largo."
        />
      ) : data ? (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Avisos y errores" value={data.total} />
            <Stat label="Fallos distintos" value={data.groups.length} />
            <Stat
              label="Código más frecuente"
              value={codes.find((entry) => entry.code)?.code ?? '—'}
            />
          </div>

          <Card padded={false}>
            <Table label="Incidencias agrupadas" minWidth="48rem">
              <THead>
                <Tr>
                  <Th className="text-right">Veces</Th>
                  <Th>Código</Th>
                  <Th className="text-right">Correos</Th>
                  <Th>Primera / última</Th>
                  <Th>Qué</Th>
                  {/* `relative`: an `sr-only` text is absolute, and with no positioned
                      ancestor it escapes the scrolling table and widens the page. */}
                  <Th className="relative">
                    <span className="sr-only">Abrir</span>
                  </Th>
                </Tr>
              </THead>
              <TBody>
                {data.groups.map((group) => (
                  <Fragment key={group.fingerprint}>
                    <GroupRow
                      group={group}
                      open={open === group.fingerprint}
                      onToggle={() =>
                        setOpen((current) =>
                          current === group.fingerprint ? null : group.fingerprint,
                        )
                      }
                    />
                    {open === group.fingerprint ? (
                      <Tr>
                        <Td colSpan={6} className="bg-paper-2">
                          <Occurrences fingerprint={group.fingerprint} since={filters.since} />
                        </Td>
                      </Tr>
                    ) : null}
                  </Fragment>
                ))}
              </TBody>
            </Table>
          </Card>

          <p>
            <a
              href={errorsFileUrl(filters)}
              className="text-accent inline-flex items-center gap-1.5 text-[14px] font-medium hover:underline"
            >
              <DownloadIcon size={16} />
              Descargar todas las filas del periodo (JSONL)
            </a>
          </p>
        </>
      ) : null}
    </div>
  );
}

function GroupRow({
  group,
  open,
  onToggle,
}: {
  group: ErrorGroup;
  open: boolean;
  onToggle: () => void;
}) {
  const sample = group.sample;
  return (
    <Tr>
      <Td className="text-right align-top font-medium tabular-nums">{group.count}</Td>
      <Td className="align-top">
        {sample.code ? (
          <Badge tone={sample.level === 'WARNING' ? 'warn' : 'danger'}>{sample.code}</Badge>
        ) : (
          <span className="text-ink-4">—</span>
        )}
      </Td>
      <Td className="text-right align-top tabular-nums">{group.emails}</Td>
      <Td className="align-top text-[13px] whitespace-nowrap">
        {formatDate(whenFormat, group.first_at)}
        <br />
        <span className="text-ink-3">{formatDate(whenFormat, group.last_at)}</span>
      </Td>
      <Td className="align-top text-[13px]">
        <span className="block">{sample.message}</span>
        <span className="text-ink-3 block font-mono text-[12px] break-all">
          {sample.logger} · {group.processes.join(', ')} · {group.releases.join(', ')}
        </span>
        {sample.exception_type ? (
          <span className="text-danger block font-mono text-[12px] break-all">
            {sample.exception_type}: {sample.exception_message}
          </span>
        ) : null}
      </Td>
      <Td className="text-right align-top">
        <Button variant="secondary" size="sm" onClick={onToggle} aria-expanded={open}>
          {open ? 'Cerrar' : 'Ver'}
        </Button>
      </Td>
    </Tr>
  );
}

function Occurrences({ fingerprint, since }: { fingerprint: string; since: string }) {
  const rows = useErrorOccurrences(fingerprint, since);
  if (rows.isPending) return <Spinner label="Cargando apariciones…" />;
  if (rows.isError) {
    return <ErrorMessage error={rows.error}>No se pudieron cargar las apariciones</ErrorMessage>;
  }
  return (
    <ul className="flex flex-col gap-2" aria-label="Apariciones">
      {rows.data.map((row) => (
        <Occurrence key={row.id} row={row} />
      ))}
    </ul>
  );
}

function Occurrence({ row }: { row: ErrorEvent }) {
  const error = typeof row.context?.error === 'string' ? row.context.error : null;
  return (
    <li className="text-[13px]">
      <span className="tabular-nums">{formatDate(whenFormat, row.occurred_at)}</span>
      <span className="text-ink-3"> · {row.process}</span>
      {row.mailbox ? <span className="text-ink-3"> · {row.mailbox}</span> : null}
      {row.email_id ? (
        <>
          {' · '}
          <Link to={`/correos/${row.email_id}`} className="text-accent hover:underline">
            Ver el correo
          </Link>
        </>
      ) : null}
      {error ? <span className="block font-mono text-[12px] break-all">{error}</span> : null}
      <TechnicalDetails text={row.traceback} />
    </li>
  );
}
